/*
 * Two carousels behind one set of arrows.
 *
 * Desktop drives the track with a transform so the 400ms easing in the design
 * is exact. Mobile leaves the viewport as a native scroll-snap scroller —
 * momentum and rubber-banding stay native — and the arrows just scroll it.
 * The mode flips at the same 900px breakpoint the CSS uses; whichever mode is
 * inactive gets its state cleared so the two never stack up. The two queries
 * have to agree exactly: where the CSS is in transform mode the viewport is
 * overflow:hidden, so a scrollTo there moves nothing and the arrows go dead.
 */
const DESKTOP_QUERY = '(min-width: 900px)';
class StandaloneReviews extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-reviews-viewport]');
    this.track = this.querySelector('[data-reviews-track]');
    this.prevButton = this.querySelector('[data-reviews-prev]');
    this.nextButton = this.querySelector('[data-reviews-next]');
    this.slides = this.track ? Array.from(this.track.children) : [];

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.index = 0;
    this.scrollFrame = null;
    this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onPrev = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.onScroll = () => this.queueScrollSync();
    this.onRefresh = () => this.refresh();
    this.onKeydown = (event) => this.handleKey(event);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.onKeydown);
    this.desktopQuery.addEventListener('change', this.onRefresh);

    // Watches the viewport rather than window resize so the theme editor
    // adding or removing a review re-measures too.
    this.resizeObserver = new ResizeObserver(this.onRefresh);
    this.resizeObserver.observe(this.viewport);

    this.refresh();
  }

  disconnectedCallback() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    if (this.viewport) {
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('keydown', this.onKeydown);
    }
    if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onRefresh);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  /* One card plus one gap — the distance a single arrow press travels. Measured
     rather than hard-coded because both breakpoints size cards in percentages. */
  get step() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.slides[0].getBoundingClientRect().width + gap;
  }

  /* How far the track can travel before the last card is flush with the right
     edge. Resolves to 0 on desktop while three cards fill the 1240 viewport,
     which is what leaves both arrows disabled at that width. */
  get maxIndex() {
    const step = this.step;
    if (step <= 0) return 0;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const visible = Math.max(1, Math.round((this.viewport.clientWidth + gap) / step));
    return Math.max(0, this.slides.length - visible);
  }

  move(direction) {
    this.goTo(this.index + direction);
  }

  /* Left/right step the rail once the viewport has focus. On mobile the browser
     would already scroll a native scroller by a short distance; preventDefault
     hands that over to goTo so a key press and an arrow press travel the same
     one-card step. */
  handleKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.move(event.key === 'ArrowLeft' ? -1 : 1);
  }

  goTo(index) {
    const target = Math.min(Math.max(index, 0), this.maxIndex);
    this.index = target;

    if (this.desktopQuery.matches) {
      this.track.style.transform = `translate3d(${-target * this.step}px, 0, 0)`;
    } else {
      this.viewport.scrollTo({
        left: target * this.step,
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
      });
    }

    this.updateButtons();
  }

  /* Native scrolling and swiping move the mobile carousel without going through
     goTo, so the index is read back off the scroller to keep the arrows honest. */
  queueScrollSync() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (this.desktopQuery.matches) return;

      const step = this.step;
      if (step <= 0) return;

      this.index = Math.min(Math.round(this.viewport.scrollLeft / step), this.maxIndex);
      this.updateButtons();
    });
  }

  refresh() {
    this.index = Math.min(this.index, this.maxIndex);

    if (this.desktopQuery.matches) {
      // Any scroll offset left over from mobile would compound with the transform.
      this.viewport.scrollLeft = 0;
      this.track.style.transform = `translate3d(${-this.index * this.step}px, 0, 0)`;
    } else {
      this.track.style.transform = '';
    }

    this.updateButtons();
  }

  updateButtons() {
    const max = this.maxIndex;
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= max;
  }
}

customElements.define('standalone-reviews', StandaloneReviews);
